import { Button, Icon, Loading, SidebarNavTrigger, useIsMobile } from "@aidkitorg/component-library";
import { evalConditional, evalDistroConditional } from "@aidkitorg/roboscreener/lib/compute/util";
import { PublicConfig } from "@aidkitorg/types/lib/config";
import { Disclosure } from "@headlessui/react";
import { ArrowLeftCircleIcon, ArrowLongLeftIcon, ArrowLongRightIcon, ArrowRightCircleIcon, CheckIcon, ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import React, { MutableRefObject, useContext, useState, useRef, useMemo, useEffect, PropsWithChildren, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { useCookies } from "react-cookie";
import { useLocation, useParams } from "react-router-dom";
import { AICoachProvider, useAICoach } from "./AICoach/AICoachContext";
import { AICoachButton, AICoachChatWindow } from "./AICoach/AICoachUI";
import * as v0 from "@aidkitorg/types/lib/survey";
import { QuestionNode, SurveyQuestion, TerminalQuestionNode } from "./Applicant";
import { Sections, FooterLink, Question, useLinkKey } from "./Apply";
import { LanguageDropdown } from "./Components/LanguageDropdown";
import { RoboScreener } from "./Components/Roboscreener";
import { useScrollContext } from "./Components/ThreeColumnPage";
import InterfaceContext, { AuthContext, ConfigurationContext, PublicConfigurationContext, SupportedLanguage } from "./Context";
import { ApplicantContext } from "./Contexts/ApplicantContext";
import { ApplicationStateContext, ApplicationStateProvider, useApplicationStateReducer } from "./Contexts/ApplicationStateContext";
import { ScrollProvider, useVerticalScrollBehavior } from "./Contexts/ScrollContext";
import { useViewport, ViewportProvider } from "./Contexts/ViewportContext";
import { HelpButton } from "./HelpButton";
import { useLocalizedStrings } from "./Localization";
import { InfoDict, QuestionOption } from "./Questions/Props";
import { safeParse, safeParseValidatedFormula, useMarkdown, languageContent, SpacedSpinner, removeAllButSpecificCookies, AidKitLogo, MiniAidKitLogo, shouldIncludeQuestionInAppState } from "./Util";
import { useModularMarkdown } from "./Hooks/ModularMarkdown";
import { AsyncMediaProvider } from "./Contexts/AsyncMediaContext"
import { SurveySectionsProvider, useSurveySections } from "./Contexts/SurveySectionsContext";
import { RoboScreenerProvider } from "./Contexts/RoboScreenerContext";
import { ReviewDetails, ReviewNav } from "./ReviewWrapper";

export function checkQuestionComplete(question: Question, info: InfoDict, complete: boolean) {
  // Assume things are complete unless found otherwise
  question.currentlyEnabled = complete;
  question.complete = true;
  question.lastVisibleQuestion = false;
  
  if (question['Field Type'] === 'Likert' || question['Field Type'] === 'Number With Unit') {
    const metadata = safeParse(question['Metadata'] || '{}') as { questions: v0.Likert['questions'], choices: v0.Likert['choices'] };
    if (Array.isArray(metadata?.questions)) {
      const questions = metadata.questions;
      if (
        (question["Additional Options"] || []).indexOf("Optional") !== -1 ||
        questions.every((question: any) => !!info[question.targetField])
      ) {
        question.complete = true;
      } else {
        complete = false;
        question.complete = false;
        question.lastVisibleQuestion = true;
      }
    } else {
      const answerData = question['Target Field'] && info[question['Target Field']] ? info[question['Target Field']] : info[metadata.questions.targetField];
      const answerField = metadata.questions.answerField;
      const possibleAnswers = metadata.choices.map(c => c.value);
      if (answerData) {
        const parsedData = safeParse(answerData);
        if (Array.isArray(parsedData)) {
          const qComplete = parsedData.every(v => {
            return answerField && possibleAnswers.includes(v[answerField])
          });
          question.complete = qComplete;
          complete = qComplete;
        }
      }
    }
  } else if (question['Field Type'] === 'Ranking' && question['Target Field']) {
    const metadata = safeParse(question['Metadata'] || '{}');
    // Verify that the required number of choices in the ranking have been selected
    if (!info[question['Target Field']] || safeParse(info[question['Target Field']]!, []).filter((val: string) => !!val).length < metadata.num_choices) {
      if (!(question['Additional Options'] || []).includes('Optional')) {
        question.complete = false;
        complete = false;
        question.lastVisibleQuestion = true;
      }
    }
  } else if (question['Field Type'] === 'Single Select' || question['Field Type'] === 'Multiple Select') {
    if (updateVisibilityForSelect(question, info)) {
      question.complete = true;
    } else {
      if ((question['Additional Options'] || []).indexOf('Optional') === -1) {
        question.complete = false;
        complete = false;
        question.lastVisibleQuestion = true;
      }
    }
  } else if (question['Field Type'] === 'Liveness Detection' && question['Target Field']) {
    // console.log("Checking liveness", question["Target Field"], info[question["Target Field"]]);
    if (!info[question['Target Field']] || info[question['Target Field']]?.includes('pending')) {
      if (!(question['Additional Options'] || []).includes('Optional')) {
        question.complete = false;
        complete = false;
        question.lastVisibleQuestion = true;
      }
    }
  } else if (question['Target Field']) {

    const hasAlert = (question['Additional Options'] || []).some(option => option.startsWith('Alert:'));
    
    if (((question['Additional Options'] || []).indexOf('Optional') === -1) &&
      ((question['Additional Options'] || []).indexOf('Advisory') === -1) &&
      ((question['Additional Options'] || []).indexOf('Hidden') === -1) &&
      !hasAlert &&
      question['Field Type'] !== 'Show Field' &&
      question['Field Type'] !== 'Show Date' &&
      !info[question['Target Field']]) {
      if (complete) {
        question.lastVisibleQuestion = true;
        complete = false;
      }
      question.complete = false;
    }
  } else if (question['Field Type'] === 'Explanation') {
    if (question['Additional Options']?.includes('Hidden')) {
      question.lastVisibleQuestion = false;
      question.currentlyEnabled = false;
      complete = true;
    }
  }
  return complete;
}

type SubmitInfo = (submit_key?: string, options?: { info: InfoDict }) => Promise<any>;

async function updateVisibility(sections: Sections, info: InfoDict) {
  let completeSoFar = true;
  let previousComplete = true;
  for (const section of sections?.sections || []) {
    // console.debug("Updating visibility for section: ", section["English Content"]);
    section.currentlyEnabled = completeSoFar;
    section.currentlyVisible = await section.computeVisible(info);
    // console.debug("Visibility is: ", section.currentlyVisible);
    if (section.currentlyVisible) {
      let complete = true;
      let lastQ = null;
      for (const question of section.Questions) {
        const enabled = await question.computeEnabled(info);
        // Uncomment for debugging
        // console.log("Question computeEnabled for question " + question.Question + ": " + enabled);
        if (enabled) {
          lastQ = question;

          // Mark completeness for a question with sub-questions based on whether all sub-questions are complete
          complete = checkQuestionComplete(question, info, complete);
          // console.log("Checking question: ", question, complete);
        } else {
          question.currentlyEnabled = false;
        }
      }
      if (complete && lastQ && lastQ !== section.Questions[section.Questions.length - 1]) {
        lastQ.lastVisibleQuestion = true;
      }
      completeSoFar = completeSoFar && complete;
      section.complete = completeSoFar;
    }
    if (previousComplete) {
      section.currentlyEnabled = true;
    } else {
      section.currentlyEnabled = false;
    }
    previousComplete = completeSoFar;
  }
  // console.log("Visibility", sections)
}

function updateVisibilityForSelect(question: Question, info: InfoDict) {
  // if SelectFromData, get options from source field, else use "Options (if relevant)"
  let options: QuestionOption[] = []
  const metadata = safeParse(question['Metadata'] || '{}');
  const sourceValue = safeParse(info[metadata.source_field] || '""');
  if (metadata.source_field && Array.isArray(sourceValue)) {
    options = sourceValue.map((i: string) => ({ 'Name': i, "English Text": i } as QuestionOption));
  } else if (question["Options (if relevant)"]) {
    options = question["Options (if relevant)"];
  }

  // For single select, return true if the target field is set for a non-other question,
  // or if the target field is set to other and the other field is set.
  if (question['Field Type'] === 'Single Select') {
    for (const option of options) {
      if (option.Name === 'other' || option["Other Field"]) {
        if (info[question["Target Field"] || ''] === option.Name && !!info[question["Target Field"] + "_" + option.Name]) {
          return true;
        }
      } else if (info[question["Target Field"] || ''] === option.Name) {
        return true;
      }
    }
    // For multiple select, return true if all of the selected options are non-other questions,
    // or if all other fields are set.
  } else if (question['Field Type'] === 'Multiple Select') {
    const optionsString = info[question["Target Field"] || ''] || '';
    if (!optionsString) return false;

    const selectedOptions = optionsString.split(',').filter((x) => x).reduce((acc, cur) => { acc[cur] = true; return acc; }, {} as Record<string, boolean>);

    const opts = (options).filter((opt) => selectedOptions[opt.Name]);

    if (!opts.length) return false;

    for (const option of opts) {
      if ((option.Name === 'other' || option["Other Field"]) && !info[question["Target Field"] + "_" + option.Name]) {
        return false;
      }
    }
    return true
  } else {
    throw new Error('updateVisibilityForSelect called on non-select question');
  }
  return false;
}

export function ModularQuestionPage(props: Parameters<typeof ModularQuestionPageInner>[0]) { 
  return <ApplicationStateProvider>
    <SurveySectionsProvider sections={props.sections}>
      <ViewportProvider>
        <ScrollProvider scrollTarget={window} throttleMs={300}>        
          <AICoachProvider>
            <ModularQuestionPageInner {...props} />  
          </AICoachProvider>
        </ScrollProvider>
      </ViewportProvider>      
    </SurveySectionsProvider>
  </ApplicationStateProvider>;
}

export function ModularQuestionPageInner(props: {
  sections: Sections,
  uid?: string,
  viewInfo?: InfoDict,
  submit?: SubmitInfo,
  loadInfo?: (info: InfoDict, token: string, submitted?: boolean) => void,
  shouldFastForward?: MutableRefObject<boolean | undefined>,
  sequential: boolean,
  noHistory?: true,
  noNavBar?: true,
  branding?: {
    name?: string,
    logo?: string,
  },
  recompute?: number
  Viewer?: 'applicant' | 'screener',
  checkpoint?: (params: { form_name: string, info: Record<string, string | undefined> }) => Promise<any>,
  blockFurtherEdits?: {
    block_when: v0.BooleanExpr,
    blocked_message: v0.RichText,
    block_mode?: 'full' | 'read_only'
  },
  warnOnIncompleteSurvey?: {
    submissionField?: string,
    warningMessage?: v0.RichText,
  },
  applicantPortal?: boolean,
  review?: ReviewDetails
}) {
  const authContext = useContext(AuthContext);
  const context = useContext(InterfaceContext);
  const config = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const { setNavigateToSection } = useSurveySections();
  
  const [roboscreener, setRoboscreener] = useState(null as RoboScreener | null);
  const [refresh, setRefresh] = useState(0);
  const [surveyLoaded, setSurveyLoaded] = useState(false);
  const [isLoadingNewSection, setIsLoadingNewSection] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [disableEdits, setDisableEdits] = useState<'full' | 'read_only' | 'none'>('none');
  const [showWarning, setShowWarning] = useState(false);
  const warningDisplayed = useRef<boolean>(false);
  const [visibleSections, setVisibleSections] = useState<Sections['sections']>([]);
  const location = useLocation();
  const AICoach = useAICoach();
  const staticFalseFn = useRef(() => false);
  const { info, update: setInfo } = useContext(ApplicantContext);
  const [showChatWindow, setShowChatWindow] = useState<boolean>(false);
  const { isMinWidth } = useViewport();
  const linkKey = useLinkKey();
  const [submitting, setSubmitting] = useState(false);

  const authToken = authContext?.token?.() ?? linkKey;

  const params = new URLSearchParams(window.location.search);
  const key = params.get('key');
  const reactParams = useParams() as Record<string, string>;
  const dynamoAppId = reactParams.dynamoAppId;

  // Survey Options: Warn on Incomplete Survey
  // Memoize the specific submission field in the info payload
  // so that we can isolate the useEffect to only run if the
  // specific key changes in info
  const surveySubmitted = useMemo(() => {
    if (props.warnOnIncompleteSurvey?.submissionField) {
      return info?.[props.warnOnIncompleteSurvey.submissionField];
    }
    return undefined;
  }, [info, props.warnOnIncompleteSurvey]);

  const isMobile = useIsMobile()

  // Survey Options: Warn on Incomplete Survey
  // If enabled, show a warning to user if they leave the
  // browser and have not yet submitted their survey
  // If on mobile, show it on page load instead
  useEffect(() => {
    const showModal = () => {
      if (props.warnOnIncompleteSurvey?.submissionField
        && props.Viewer !== 'screener'
        && warningDisplayed.current === false
        && surveySubmitted === undefined) {
        // Only show this warning once to the user
        setShowWarning(true);
        warningDisplayed.current = true;
      }
    };

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 || e.clientX <= 0 || e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
        showModal();
        // Cleanup event listeners ASAP after the first warning
        document.removeEventListener('mouseleave', handleMouseLeave);
      }
    };

    if (props.warnOnIncompleteSurvey?.submissionField) {
      if (isMobile) {
        showModal();
      } else {
        document.addEventListener('mouseleave', handleMouseLeave);
      }
    }

    // Cleanup function to remove the event listener when warnOnIncompleteSurvey is disabled or the component unmounts
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [props.warnOnIncompleteSurvey, surveySubmitted, isMobile]);

  useEffect(() => {
    // When the pathname changes because the user is navigating to a different section/subsurvey,
    // mark us as loading a new section
    setIsLoadingNewSection(true);
  }, [location.pathname]);
  
  // this function will be "passed up" into the SurveySectionsContext
  // to make the ability to switch sections more available across the app
  const navigateToSectionById = useCallback(
    (id: string) => {
      if (!id) return;
      const sections = visibleSections ?? [];
      for (const section of sections) {
        if (section.id === id) {
          return setSelectedTab(sections.indexOf(section));
        }
      }
      console.warn("No visible section found for id", id);
    },
    [visibleSections, setSelectedTab]
  );
  
  useEffect(() => { 
    setNavigateToSection(navigateToSectionById);
  }, [navigateToSectionById])

  useEffect(() => {
    // When we're loading a new section and the survey has finished loading,
    // reset the selected tab to be the first tab.
    if (isLoadingNewSection && surveyLoaded) {
      setSelectedTab(0);
      setIsLoadingNewSection(false);
    }
  }, [isLoadingNewSection, surveyLoaded]);

  // Hash-based navigation: Handle #step1, #step2, etc. in URL
  const hasInitialHashCheck = useRef(false);

  useEffect(() => {
    if (!visibleSections?.length) return;

    const navigateToHashStep = () => {
      const hash = window.location.hash.slice(1);
      if (!hash.startsWith('step')) return;

      const stepIndex = parseInt(hash.replace('step', ''), 10) - 1;

      if (stepIndex >= 0 &&
          stepIndex < visibleSections.length &&
          visibleSections[stepIndex].currentlyEnabled &&
          stepIndex !== selectedTab) {
        setSelectedTab(stepIndex);
      }
    };

    // Check hash on initial load
    if (!hasInitialHashCheck.current) {
      navigateToHashStep();
      hasInitialHashCheck.current = true;
    }

    // Listen for hash changes
    window.addEventListener('hashchange', navigateToHashStep);
    return () => window.removeEventListener('hashchange', navigateToHashStep);
  }, [visibleSections, selectedTab]);

  useEffect(() => {
    // Keep hash in sync with current step for sharing links
    if (!visibleSections?.length || props.noHistory || props.noNavBar) return;

    const expectedHash = `#step${selectedTab + 1}`;
    if (window.location.hash !== expectedHash) {
      window.history.pushState(null, "", expectedHash);
    }
  }, [visibleSections?.length, selectedTab, props.noHistory, props.noNavBar]);

  useEffect(() => {
    // console.log("Creating rs", props.sections);
    if (!props.sections.sections) return;

    const formulas: Record<string, string> = {};
    const conditionOns: Record<string, string> = {};
    for (const section of props.sections.sections || []) {
      if (!section) continue;
      let computeVisible = null;
      const metadata = typeof section.Metadata === 'string' && safeParse(section.Metadata || "{}");
      if (metadata?.hideNextButton) {
        section.hideNextButton = metadata?.hideNextButton;
      }
      if (metadata?.hidden) {
        computeVisible = () => false;
      }
      else if (metadata?.conditional) {
        computeVisible = async (info: InfoDict) => await evalConditional(metadata.conditional, info, {}, {}, {
          viewInfo: props.viewInfo
        });
      } else if (section['Conditional On']) {
        if (section['Conditional On Value']) {
          if (section['Conditional On Value'][0] === '!') {
            computeVisible = (info: InfoDict) => !info[section['Conditional On']!];
          } else {
            computeVisible = (info: InfoDict) => !!(info[section['Conditional On']!]
              && (section['Conditional On Value'] || [] as string[]).indexOf(info[section['Conditional On']!] || '') !== -1);
          }
        } else {
          computeVisible = (info: InfoDict) => !!info[section['Conditional On']!];
        }
      } else {
        computeVisible = (info: InfoDict) => true;
      }
      section.computeVisible = computeVisible;

      for (const question of section.Questions) {
        let computeEnabled = null;
        if (question['Field Type'] === "Computed") {
          formulas[question['Target Field']!] = safeParse(question['Metadata'] || '{}').formula;
          conditionOns[question['Target Field']!] = safeParse(question['Metadata'] || '{}')?.conditional || '';
        }
        if (question['Field Type'] === "Validated") {
          formulas[question['Target Field']!] = '(() => {' + safeParseValidatedFormula(question['Metadata'] || '') + '})()';
        }
        if (question['Field Type'] === "Payment") {
          computeEnabled = (info: InfoDict) => !!info[question['Target Field']!];
        } else if (question['Field Type'] === 'Resume Widget') {
          computeEnabled = (info: InfoDict) => !key;
        } else if (question['Field Type'] === 'Qualtrics Response Handler') {
          computeEnabled = () => false;
        } else if (question['Field Type'] === 'Notification' && !safeParse(question['Metadata'] || '{}').inline) {
          computeEnabled = () => false;
        } else {
          if (question['Conditional On']) {
            // console.log(question['Conditional On'], question['Conditional On Value']);
            if (question['Conditional On Value']) {
              if (question['Conditional On Value'][0] === '!') {
                computeEnabled = (info: InfoDict) => !info[question['Conditional On']!];
              } else {
                computeEnabled = (info: InfoDict) => {
                  return !!(info[question['Conditional On']!] && (question['Conditional On Value'] || [] as string[]).indexOf(info[question['Conditional On']!] || '') !== -1);
                }
              }
            } else {
              computeEnabled = (info: InfoDict) => !!info[question['Conditional On']!];
            }
          } else {
            // Check if conditional in metadata, ensuring metadata is an object first for legacy Validated type support.
            if (question['Metadata'] && safeParse(question['Metadata'], 'safeReturn') !== 'safeReturn') {
              const metadata = safeParse(question['Metadata'] || '{}') as v0.LegacyCommonProperties;
              if (metadata.conditional) {
                computeEnabled = async (info: InfoDict) => {
                  // console.debug("Question computeEnabled for question " + question.Question + ": " + metadata.conditional, info);
                  const result = await evalConditional(metadata.conditional!, info, {}, {}, {
                    viewInfo: props.viewInfo
                  });
                  return !!result;
                }

              } else {
                computeEnabled = (info: InfoDict) => true;
              }
            } else {
              computeEnabled = (info: InfoDict) => true;
            }
          }
        }
        question.computeEnabled = computeEnabled;
      }
    }
    if (!roboscreener) {
      const rs = new RoboScreener('', formulas, conditionOns, applicationStateChanged); //, Object.keys(status.applicants)[0])
      // console.log("Creating rs", rs, props.sections);
      setRoboscreener(rs);
    } else {
      // Reset deps
      roboscreener.formulas = formulas;
      roboscreener.conditionOns = conditionOns;
      roboscreener.collectedDependencies = false;
      // Kick off a recompute in the usual spot
      setInfo({ ...info })
    }
    return () => {
      // cleans up a memory leak
      for (const section of props.sections.sections || []) {
        if (!section) continue;
        section.computeVisible = staticFalseFn.current;
        for (const question of section.Questions) {
          question.computeEnabled = staticFalseFn.current;
        }
      }
    }
  }, [props.sections])

  useEffect(() => {
    if (roboscreener && props.recompute) {
      roboscreener.collectedDependencies = false;
      setInfo({ ...info })
    }
  }, [roboscreener, props.recompute]);

  useEffect(() => {
    (async () => {
      if (roboscreener && info) {
        roboscreener.info = info;
        roboscreener.computeUpdates();
        setInfo(roboscreener.info);

        let localSelectedTab = selectedTab;
        if (props.sections) {
          await updateVisibility(props.sections, roboscreener.info);
          const localVisibleSections = (props.sections.sections || []).filter(section => section?.currentlyVisible);
          setVisibleSections(localVisibleSections);
          // console.log("localVisibleSections", localVisibleSections);
          // Don't fast forward if there's a hash in the URL (user is navigating to specific step)
          const hasStepHash = window.location.hash.startsWith('#step');

          if (props.shouldFastForward && props.shouldFastForward.current && !hasStepHash) {
            let idx = 0;
            for (const section of localVisibleSections) {
              if ((section.complete && localVisibleSections.indexOf(section) !== localVisibleSections.length - 1)) {
                idx += 1;
              } else {
                break;
              }
            }
            setSelectedTab(idx);
            localSelectedTab = idx;
            props.shouldFastForward.current = false;
          }

          if (!surveyLoaded && localVisibleSections.length && !props.shouldFastForward) {
            setSurveyLoaded(true);
          }

          setRefresh(refresh + 1);
        }
      }
    })();
  }, [info, roboscreener, props.sections]);

  useEffect(() => {

    for (const section of visibleSections ?? []) {
      applicationStateChanged({
        type: 'update-section',
        sectionId: section.id,
        update: {
          complete: !!section.complete
        }
      });
    }

  }, [visibleSections]);

  if (props.branding?.name) {
    document.title = props.branding.name;
  }

  useEffect(() => {
    (async () => {
      // Applicant viewer - block further edits
      if (props.Viewer !== 'screener' && props.blockFurtherEdits?.block_when) {
        const block = await evalDistroConditional(props.blockFurtherEdits.block_when, info || {}, false);
        setDisableEdits(block ? (props.blockFurtherEdits.block_mode ?? 'full') : 'none');
      }
      // Screener viewer - page may be read only.
      if (props.viewInfo?.readonly) {
        setDisableEdits('read_only');
      }
    })();
  }, [info, props.Viewer, props.blockFurtherEdits, props.viewInfo]);

  const md = (s: string) => s;

  const L = useLocalizedStrings();
  const warnOnIncompleteMessage = useMarkdown(props.warnOnIncompleteSurvey?.warningMessage?.[context.lang as SupportedLanguage]);
  const onClickChatButton = useCallback(() => setShowChatWindow(true), [setShowChatWindow]);

  const { applicationStateChanged } = useContext(ApplicationStateContext);

  useEffect(() => {

    // TODO(luke): need a more long-term solution. 
    // We should just know exactly what survey we're looking at without 
    // the need to interrogate the URL path.
    applicationStateChanged({
      type: 'update-survey-details',
      update: {
        currentSurveyPath: location.pathname
      }
    });

    const sections = (props.sections?.sections ?? [])
      ?.filter(Boolean)
      ?.filter(s => !(s.Metadata as any)?.['hidden']);

    for (const s of sections) {

      applicationStateChanged({
        type: 'update-section',
        sectionId: s.id,
        update: {
          name: s[context.lang] ?? s.en!,
          complete: !!s.complete,
          viewing: !!s.currentlyVisible
        }
      });

      const questions = s.Questions?.filter(shouldIncludeQuestionInAppState)

      for (const q of questions) {
        applicationStateChanged({
          type: 'change',
          questionId: q.Question,
          update: {
            sectionId: s.id,
            type: q["Field Type"],
            content: (q as any)[context.lang ?? 'en'] ?? q["English Content"],
            options: q["Options (if relevant)"]?.map(o => ({
              name: o.Name,
              content: (o as any)[context.lang ?? 'en'] ?? o["English Text"],
              terminatesApplication: o["Terminates Application"],
              fieldType: o["Select All"] ? 'select-all' : o["Other Field"] ? 'other' : 'custom-option',
            })),
            required: !q["Additional Options"]?.includes('Optional')
          }
        });
      }
    }

  }, [props.sections.sections]);
  
  useEffect(() => {
    applicationStateChanged({
      type: "update-info",
      update: info
    });
  }, [info]);

  function footer() {
    if (!publicConfig.interface?.applicantFacingFooter || publicConfig.interface.applicantFacingFooter.disable) {
      return null;
    }

    // since we're dynamically building these grid classes
    // this string exists to ensure tailwind includes these classes in the build
    "grid-cols-1 grid-cols-2 grid-cols-3 grid-cols-4 md:grid-cols-1 md:grid-cols-2 md:grid-cols-3 md:grid-cols-4";

    const isLargeScreen = isMinWidth("md");
    const footerConfig = publicConfig.interface?.applicantFacingFooter;
    const footerItems = footerConfig?.items ?? [];

    // Filter items based on screen size
    const visibleItems = footerItems.filter(item =>
      (!item.hideOnLargeScreen || !isLargeScreen) &&
      (!item.hideOnSmallScreen || isLargeScreen)
    );

    return <ApplicantFacingFooter>
      <div className="w-full">
        {!isLargeScreen ? (
          // Mobile layout
          <div className="w-full flex flex-col">
            {/* Horizontal header row */}
            <div className="flex w-full flex-nowrap">
              {visibleItems.map((item, i) => (
                <div key={i} className="flex-1">
                  <Disclosure>
                    {({ open }) => (
                      <div>
                        <Disclosure.Button className="w-full py-4">
                          <div className="flex items-center justify-start whitespace-nowrap px-2">
                            <FooterLink item={item} />
                            <Icon
                              name={open ? "ChevronUpIcon" : "ChevronDownIcon"}
                              size="sm"
                              className="ml-1 text-gray-500"
                            />
                          </div>
                        </Disclosure.Button>
                        <Disclosure.Panel className="border-t mt-2 px-4 py-2">
                          <div className="flex flex-col space-y-2 items-start">
                            {item.links?.map((link, i) => (
                              <FooterLink key={i} item={link} isNested />
                            ))}
                          </div>
                        </Disclosure.Panel>
                      </div>
                    )}
                  </Disclosure>
                </div>
              ))}
              {/* Chat button */}
              {(!isMinWidth("md") && AICoach.enabled && !!authToken && !!visibleSections?.length) && (
                <div className="px-2 mt-2">
                  <AICoachButton onClick={onClickChatButton} />
                </div>
              )}
            </div>
          </div>
        ) : (
          // Desktop grid layout
          <div className="max-w-4xl mx-auto grid grid-cols-3 gap-8 pl-6">
            {visibleItems.map((item, i) => (
              <FooterSection key={i} items={item} />
            ))}
          </div>
        )}
      </div>
    </ApplicantFacingFooter>
  }

  const { scrollToTop } = useScrollContext() || {};
  // Keep ref updated but don't trigger re-renders
  const scrollToTopFnRef = useRef(scrollToTop);
  useEffect(() => {
    scrollToTopFnRef.current = scrollToTop;
  }, [scrollToTop]);

  useEffect(() => {
    if (scrollToTopFnRef.current) {
      scrollToTopFnRef.current();
    } else {
      window.scrollTo(0, 0);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (roboscreener && context.lang && props.Viewer === 'applicant') {
      roboscreener.setKey('_lang', context.lang);
      setInfo(roboscreener.info);
    }
  }, [context.lang, roboscreener, props.Viewer]);

  const steps: Step[] = useMemo(() => {
    return (visibleSections ?? []).map((section, index, sections) => {
      const prevIndex = index - 1;
      const nextIndex = index + 1;
      return ({
        id: section.id,
        title: section[languageContent(context.lang)] || section['English Content'],
        completed: section.complete,
        visitable: section.currentlyEnabled || !props.sequential,
        visible: section.currentlyVisible || false,
        progressed: index < selectedTab + 1,
        children: <div key={'section-' + index} className={props.review ? "h-full" : md("md:pt-4 h-full")}>
          <Section
            info={info}
            hideBottomButtons={props.sections?.options?.sectionBottomButtons?.hideOnFirstSection && index === 0}
            sectionBottomButtons={props.sections?.options?.sectionBottomButtons}
            sequential={!props.sequential && !section.Metadata?.dashboard ? false : true}
            section={section}
            next={nextIndex <= sections.length - 1 ? () => setSelectedTab(nextIndex) : undefined}
            nextName={sections[nextIndex]?.[languageContent(context.lang)] || nextIndex + ''}
            previous={prevIndex >= 0 ? () => setSelectedTab(prevIndex) : undefined}
            previousName={sections[prevIndex]?.[languageContent(context.lang)] || prevIndex + ''}
            checkpoint={props.checkpoint}

            {...{
              ...(props.submit ? { submit: props.submit, submitting, setSubmitting } : {}),
              ...(props.loadInfo ? {
                loadInfo: ((...args) => {
                  if (roboscreener) roboscreener.collectedDependencies = false;
                  props.loadInfo?.(...args);
                })
              } : {})
            }}

            setInfoKey={async (key, value, valid, disqualifies) => {
              if (disableEdits !== 'none') return;

              roboscreener?.setKey(key, value);
              updateVisibility(props.sections, roboscreener?.info || {});

              // Collect all fields to reset first, then batch update
              const fieldsToReset: Record<string, string> = {};
              for (const section of props.sections?.sections || []) {
                for (const question of section.Questions) {
                  const metadata = safeParse(question['Metadata'] || '{}') as v0.LegacyCommonProperties;

                  if (metadata.resetUnreachableFields && question["Target Field"] && question["Field Type"] !== 'Show Field' && await question.computeEnabled(roboscreener?.info || {}) === false) {
                    fieldsToReset[question["Target Field"]] = "";

                    // Reset all slugs related to this target field
                    metadata.slugs?.forEach((slug) => {
                      fieldsToReset[slug] = "";
                    })
                  }
                }
              }
              // Batch reset all fields at once
              if (Object.keys(fieldsToReset).length > 0) {
                roboscreener?.setKeys(fieldsToReset);
              }

              setInfo(roboscreener?.info || {});

              if (!props.sequential && props.submit && roboscreener?.info) {
                props.submit(undefined, { info: roboscreener.info })
              }
            }}
            uid={props.uid}
            Viewer={props.Viewer || 'applicant'}
            disableInputs={!!(props.viewInfo?.readonly) || disableEdits === 'read_only'}
            blockFurtherEdits={disableEdits === 'full'}
            hideNextButton={section.hideNextButton}
            viewInfo={props.viewInfo}
            review={props.review}
          />
        </div>
      })
    })
  }, [visibleSections,
    props.sections,
    props.submit,
    props.sequential,
    props.uid,
    props.Viewer,
    props.viewInfo,
    context.lang,
    roboscreener?.info,
    selectedTab,
    setSelectedTab]);

  useEffect(() => {
    const current = steps?.[selectedTab];
    for (const step of steps) {
      applicationStateChanged({
        type: 'update-section',
        sectionId: step.id,
        update: {
          name: step.title,
          visitable: step.visitable,
          viewing: step.id === current?.id,
          complete: step.completed,
        }
      });
    }
  }, [steps, selectedTab, applicationStateChanged]);

  // unsubmitted applicants are always view only - show default message.
  const blockEditBanner = dynamoAppId
    ? L.applicant.view_only
    : (disableEdits !== 'none' && !submitting && props.blockFurtherEdits?.blocked_message)
      ? props.blockFurtherEdits?.blocked_message
      : undefined;

  const mainContent = visibleSections?.length ? <>
    {props.review ? (
      <div className="h-full">
        <ReviewNav
          title={props.review.title}
          steps={steps}
          sequential={true}
          currentStep={selectedTab}
          setCurrentStep={setSelectedTab}
          info={info}
          reviewDetails={props.review}
          uid={props.uid}
        />
        <HelpButton />
      </div>
    ) : (
      <div className="max-w-4xl mx-auto applicant-led pb-36">
        <NavBar noNavBar={props.noNavBar} branding={props.branding}
          steps={steps}
          sequential={true}
          currentStep={selectedTab}
          setCurrentStep={setSelectedTab}
          blockedEditBanner={blockEditBanner}
          languages={(config.languages || 'en,es').split(',') as SupportedLanguage[]}
          applicantPortal={props.applicantPortal}
          info={info}
        />
        {props.warnOnIncompleteSurvey && (
          <Modal show={showWarning} onHide={() => setShowWarning(false)}>
            <Modal.Body>
              {warnOnIncompleteMessage}
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={() => setShowWarning(false)}
              >
                {L.apply.continue}
              </button>
            </Modal.Footer>
          </Modal>
        )}
        <HelpButton />
      </div>
    )}
    {/* ai coach button (floating, desktop only) */}
    {
      (isMinWidth("md") && AICoach.enabled && !!authToken) && <div className="hidden md:flex sticky pointer-events-none bottom-10 mb-10 z-60 justify-end ltr:mr-10 rtl:ml-10">
        <AICoachButton onClick={onClickChatButton} />
      </div>
    }
    {footer()}
    {/* chat window */}
    {(showChatWindow) && <AICoachChatWindow onClickMinimize={() => setShowChatWindow(false)} />}
  </> : (
    <div className="flex justify-center items-center h-screen w-full">
      <SpacedSpinner className="h-20 w-20 text-gray-400" /> {/* TODO: replace with PageLoading Component */}
    </div>
  );

  return (
    <RoboScreenerProvider roboscreener={roboscreener}>
      <AsyncMediaProvider>
        {mainContent}
      </AsyncMediaProvider>
    </RoboScreenerProvider>
  );
}

function FooterSection({ items }: { items: NonNullable<PublicConfig["interface"]["applicantFacingFooter"]>["items"][number] }) {
  const { isMinWidth } = useViewport();
  const isDesktop = isMinWidth("md");

  if (isDesktop) {
    return (
      <div className="flex flex-col space-y-2 self-start">
        <FooterLink item={items} />
        {items.links?.map((link, i) => (
          <FooterLink key={i} item={link} isNested />
        ))}
      </div>
    );
  }

  return (
    <div className="flex-1 relative">
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className="w-full text-center py-4">
              <div className="flex items-center justify-center whitespace-nowrap px-4">
                <FooterLink item={items} />
                <Icon
                  name={open ? "ChevronUpIcon" : "ChevronDownIcon"}
                  size="sm"
                  className="ml-1 text-gray-500"
                />
              </div>
            </Disclosure.Button>
            <Disclosure.Panel className="absolute left-0 right-0 bg-white border-t mt-2 px-4 py-2 w-full z-10">
              <div className="flex flex-col space-y-2">
                {items.links?.map((link, i) => (
                  <FooterLink key={i} item={link} isNested />
                ))}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  );
}

function ApplicantFacingFooter({ children }: PropsWithChildren) {
  const publicConfig = useContext(PublicConfigurationContext);
  const { isMinWidth } = useViewport();
  const { scrollDirection, isScrolling } = useVerticalScrollBehavior({ threshold: 50 });
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const footerRef = useRef<HTMLDivElement>(null);
  const [shouldRenderContent, setShouldRenderContent] = useState<boolean>(true);

  const position = footerRef.current ? getComputedStyle(footerRef.current).position : null;
  const useCollapse = publicConfig.interface?.applicantFacingFooter?.hideOnScroll && !isMinWidth("md");
  useEffect(() => {
    if (useCollapse && isScrolling && footerRef.current && (position === "sticky" || position === "fixed")) {
      setCollapsed(scrollDirection === "down")
    }
  }, [useCollapse, position, footerRef.current, scrollDirection, isScrolling]);

  useEffect(() => {
    if (footerRef.current) {
      footerRef.current.style.transform = `translateY(${(useCollapse && collapsed) ? footerRef.current.offsetHeight : 0}px)`
    }
  }, [collapsed, footerRef.current])

  return <footer 
    onTransitionEnd={() => setShouldRenderContent(!collapsed)} 
    ref={footerRef} 
    className="fixed touch-none md:static md:touch-auto flex bottom-0 w-full z-40 bg-white border-gray-200 border-solid border-t border-b-0 border-l-0 border-r-0 p-2.5 md:p-4 transition duration-200 ease-in-out">
    {shouldRenderContent && children}
  </footer>;
}

function Section(props: {
  section: Required<Sections>['sections'][number],
  sequential: boolean,
  next?: () => void,
  nextName?: string,
  previous?: () => void,
  previousName?: string,
  submit?: () => Promise<void>,
  submitting?: boolean,
  setSubmitting?: (isSubmitting: boolean) => void,
  loadInfo?: (info: InfoDict, token: string, submitted?: boolean) => void,
  setInfoKey: (
    key: string,
    value: any,
    valid: boolean,
    disqualifies: boolean
  ) => Promise<void>,
  info?: any,
  uid?: string,
  Viewer: 'applicant' | 'screener',
  blockFurtherEdits: boolean,
  disableInputs?: boolean,
  hideNextButton?: boolean,
  hideBottomButtons?: boolean,
  viewInfo?: Record<string, string | undefined>,
  sectionBottomButtons?: { logoutRedirectPath: string },
  checkpoint?: (params: { form_name: string, info: Record<string, string | undefined> }) => Promise<any>,
  review?: ReviewDetails
}) {

  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const sessionStorage = context.sessionStorage;
  const config = useContext(PublicConfigurationContext);
  const bottomButtons = props.sectionBottomButtons ?? config.interface?.sectionBottomButtons;

  const { localid } = useParams<{ localid?: string }>();

  // Generate infoValid object by mapping the truthiness of info object properties
  const infoValid = useMemo(() => {
    const infoValid: Record<string, boolean> = {};
    for (const key in props.info) {
      infoValid[key] = !!props.info[key];
    }
    return infoValid;
  }, [props.info]);

  const [unstuckIds, setUnstuckIds] = useState<string[]>([]);
  const clearApplicantSticky = (id: string) => setUnstuckIds(prevIds => [...prevIds, id]);
  const shouldObserveSticky = useCallback((id: string) => !unstuckIds.includes(id), [unstuckIds]);
  const cookieName = encodeURIComponent('key:' + window.location.pathname);
  const [cookies,] = useCookies([cookieName]);

  // State for resizable/draggable columns
  const [leftColumnWidth, setLeftColumnWidth] = useState(50);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMinWidth } = useViewport();
  const isDesktop = isMinWidth("md");

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  // Add/remove event listeners for resize
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Constrain between 20% and 80%
      if (newWidth >= 20 && newWidth <= 80) {
        setLeftColumnWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  if (props.blockFurtherEdits) {
    return <></>;
  }

  // Udpated Review UI with full-width and pinned column support
  if (props.review) {
    const hasOption = (question: Question, optionName: string) =>
      (question["Additional Options"] || []).indexOf(optionName) !== -1;

    const questionsWithIndices = props.section.Questions.map((q: Question, idx: number) => ({ question: q, index: idx }));

    const firstPinnedIndex = questionsWithIndices.findIndex(entry =>
      entry.question.currentlyEnabled && hasOption(entry.question, "Pin to Left Column")
    );
    const hasPinnedQuestions = firstPinnedIndex !== -1;

    const fullWidthQuestionsWithIndices = hasPinnedQuestions
      ? questionsWithIndices.filter((entry, idx) =>
        entry.question.currentlyEnabled &&
        hasOption(entry.question, "Force Full Width") &&
        idx < firstPinnedIndex
      )
      : [];

    const fullWidthIndices = new Set(fullWidthQuestionsWithIndices.map(entry => entry.index));
    const pinnedIndices = new Set(
      questionsWithIndices
        .filter(entry => entry.question.currentlyEnabled && hasOption(entry.question, "Pin to Left Column"))
        .map(entry => entry.index)
    );

    const pinnedQuestionsWithIndices = questionsWithIndices.filter(entry =>
      pinnedIndices.has(entry.index) && !fullWidthIndices.has(entry.index)
    );

    const regularQuestionsWithIndices = hasPinnedQuestions
      ? questionsWithIndices.filter(entry =>
        entry.question.currentlyEnabled &&
        !pinnedIndices.has(entry.index) &&
        !fullWidthIndices.has(entry.index)
      )
      : questionsWithIndices.filter(entry => entry.question.currentlyEnabled);

    const renderQuestion = (question: any, index: number, isPinnedColumn?: boolean) => {
      const { keepVisibleForReview }: { keepVisibleForReview?: boolean } = safeParse(question.Metadata || '{}');

      return (
        <div
          className={keepVisibleForReview && shouldObserveSticky(question.Question) ? 'sticky top-0 bg-white z-[9]' : ''}
          key={props.section["English Content"] + "-question-" + index}
        >
          {React.createElement(SurveyQuestion, {
            ...question,
            index,
            uid: props.uid,
            Viewer: props.Viewer || 'applicant',
            setInfoKey: props.setInfoKey,
            info: props.info,
            infoValid,
            Submit: props.submit,
            submitting: props.submitting,
            setSubmitting: props.setSubmitting,
            LoadInfo: props.loadInfo,
            pageNext: props.next,
            provisional: true,
            selfServe: true,
            blockQuestionEdits: props.disableInputs || props.blockFurtherEdits,
            lastQuestion: (!props.next || !props.sequential || props.hideNextButton) && index === props.section.Questions.length - 1 || question.lastVisibleQuestion,
            clearApplicantSticky,
            viewInfo: props.viewInfo,
            isReview: true,
            hasPinnedQuestions,
            isPinnedColumn,
          })}
        </div>
      );
    };

    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto">
          {/* Full-width questions at the top */}
          {fullWidthQuestionsWithIndices.length > 0 && (
            <div>
              {fullWidthQuestionsWithIndices.map(entry =>
                renderQuestion(entry.question, entry.index)
              )}
            </div>
          )}

          {/* Two-column layout or regular layout */}
          {hasPinnedQuestions ? (
            <div ref={containerRef} className="flex flex-col md:flex-row relative md:pb-96">
              {/* Left column - sticky, scrolls on its own */}
              <div
                className="w-full md:self-start flex-shrink-0 pt-2 md:sticky md:top-0 md:max-h-screen md:overflow-y-auto border rounded-md ms-2"
                style={
                  isDesktop
                    ? { width: `${leftColumnWidth}%`, minWidth: 0, paddingBottom: 'calc(100vh - 150px)' }
                    : undefined
                }
              >
                {pinnedQuestionsWithIndices.map(entry => renderQuestion(entry.question, entry.index, true))}
              </div>

              {/* Resize handle */}
              <div
                className="cursor-col-resize"
                onMouseDown={handleMouseDown}
              >
                <div className="w-1 h-full"></div>
              </div>

              {/* Right column - scrolls with page */}
              <div
                style={isDesktop ? { width: `calc(${100 - leftColumnWidth}% - 0.5rem)`, minWidth: 0 } : undefined}
              >
                {regularQuestionsWithIndices.map(entry => renderQuestion(entry.question, entry.index))}
              </div>
            </div>
          ) : (
            <>
              {regularQuestionsWithIndices.map(entry =>
                renderQuestion(entry.question, entry.index)
              )}
            </>
          )}
        </div>

        {/* Review navigation buttons - fixed at bottom, outside scroll */}
        <div className="flex-shrink-0 w-full bg-white border-t border-gray-200 p-3 z-10 h-16">
          <div className="flex justify-between items-center mx-auto h-full">
            <Button
              variant="secondary"
              onClick={() => { props.previous?.(); }}
              isDisabled={!props.previous}
              icon={<Icon name="ArrowLeftIcon" type="solid" size="sm" role="decorative" />}
            >
              {L.applicant.back}
            </Button>
            {!props.hideNextButton && (
              <Button
                variant="primary"
                onClick={() => { props.next && props.next(); }}
                isDisabled={!props.next || !props.section.complete}
                icon={<Icon name="ArrowRightIcon" type="solid" size="sm" role="decorative" />}
                iconPlacement="right"
              >
                {L.applicant.next}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Layout not configured for new Review UI
  return (
    <div className="px-6">
      <div>
        {/* Previous button */}
        {props.sequential && props.previous && (
          <QuestionNode icon={ArrowLeftCircleIcon} key={props.section["English Content"] + "-question-first"}>
            <button
              type="button"
              onClick={() => { props.previous?.(); }}
              className={
                config.experimental?.newNavigationButtonStyle ?
                  "rounded-lg border-black bg-gray-600 px-4 py-2 text-sm font-medium text-gray-50 hover:outline hover:outline-gray-500" :
                  "inline-flex items-center -mt-2 px-4 py-2 border border-gray-300  text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              }
            >
              {L.apply.previous_with_name.replace('$previous', props.previousName || '')}
            </button>
          </QuestionNode>
        )}

        {/* Render all questions in simple list */}
        {props.section.Questions.map((question, index) => {
          if (!question.currentlyEnabled) return null;

          const { keepVisibleForReview }: { keepVisibleForReview?: boolean } = safeParse(question.Metadata || '{}');

          return (
            <div
              className={keepVisibleForReview && shouldObserveSticky(question.Question) ? 'sticky top-0 bg-white z-[9]' : ''}
              key={props.section["English Content"] + "-question-" + index}
            >
              {React.createElement(SurveyQuestion, {
                ...question,
                index,
                uid: props.uid,
                Viewer: props.Viewer || 'applicant',
                setInfoKey: props.setInfoKey,
                info: props.info,
                infoValid,
                Submit: props.submit,
                submitting: props.submitting,
                setSubmitting: props.setSubmitting,
                LoadInfo: props.loadInfo,
                pageNext: props.next,
                provisional: true,
                selfServe: true,
                blockQuestionEdits: props.disableInputs || props.blockFurtherEdits,
                lastQuestion: (!props.next || !props.sequential || props.hideNextButton) && index === props.section.Questions.length - 1 || question.lastVisibleQuestion,
                clearApplicantSticky,
                viewInfo: props.viewInfo,
                isReview: false,
                hasPinnedQuestions: false,
              })}
            </div>
          );
        })}

        {/* Next button */}
        {props.sequential && props.next && props.section.complete && !bottomButtons && !props.hideNextButton && (
          <TerminalQuestionNode icon={ArrowRightCircleIcon} key={props.section["English Content"] + "-question-last"}>
            <button
              type="button"
              onClick={() => { props.next && props.next(); }}
              className={
                config.experimental?.newNavigationButtonStyle ?
                  "rounded-lg border-black bg-gray-600 px-4 py-2 text-sm font-medium text-gray-50 hover:outline hover:outline-gray-500" :
                  "inline-flex items-center -mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              }
            >
              {L.apply.next_with_name.replace('$next', props.nextName || '')}
            </button>
          </TerminalQuestionNode>
        )}

        {/* Bottom buttons - applicant facing (mobile-first with save/logout) */}
        {bottomButtons && !props.hideBottomButtons && (
          <div className="survey-question relative pb-8 text-clip mt-24">
            <div className="flex flex-col space-y-4">
              <div className="pt-1.5">
                <div className='flex justify-between space-x-2 mb-10 max-w-sm'>
                  <button type="button"
                    onClick={() => { props.previous?.(); }}
                    disabled={!props.previous}
                    className={`inline-flex items-center justify-center w-1/2 px-4 py-2 border text-sm font-medium rounded-md ${props.previous ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-100 hover:outline hover:outline-gray-500' : 'border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed'}`}
                  >
                    <ArrowLongLeftIcon className="h-4 w-4 mr-2" />
                    {L.applicant.back}
                  </button>
                  {!props.hideNextButton && <button type="button"
                    onClick={() => { props.next && props.next(); }}
                    disabled={!props.next || !props.section.complete}
                    className={`inline-flex items-center justify-center w-1/2 px-4 py-2 border text-sm font-medium rounded-md ${(props.next && props.section.complete) ? 'text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500' : 'border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed'}`}
                  >
                    {L.applicant.next}
                    <ArrowLongRightIcon className="h-4 w-4 ml-2" />
                  </button>}
                </div>

                {(sessionStorage.getItem('auth:' + localid) || cookies[cookieName]) && (
                  <div className="flex flex-justify-center max-w-sm">
                    <button type="button"
                      onClick={async () => {
                        if (confirm(L.apply.confirm_logout)) {
                          if (props.checkpoint) {
                            await props.checkpoint({
                              form_name: 'apply',
                              info: props.info || {},
                            });
                          }
                          // Clear all site cookies except auth_token. Leave that for admins doing testing.
                          removeAllButSpecificCookies(['auth_token']);
                          sessionStorage.clear()
                          window.location.href = '/' + bottomButtons.logoutRedirectPath;
                        }
                      }}
                      className="px-4 py-2 w-100 border border-gray-300 text-sm font-medium rounded-md text-indigo-600 bg-white hover:bg-gray-100 hover:outline hover:outline-gray-500"
                    >
                      {L.apply.save_and_log_out}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type Step = {
  id: string;
  title: string;
  completed?: boolean;
  progressed?: boolean;
  visitable?: boolean;
  visible: boolean;
  children?: JSX.Element;
}

type Steps = {
  branding?: {
    name?: string,
    logo?: string
  },
  steps: Step[];
  currentStep: number;
  setCurrentStep: (step: number) => void;
  sequential: boolean;
  languages: SupportedLanguage[],
  noNavBar?: true,
  blockedEditBanner?: v0.RichText | string,
  applicantPortal?: boolean,
  info?: InfoDict,
}

function NavBar(props: Steps) {
  const { scrollDirection, isScrolling } = useVerticalScrollBehavior({ threshold: 50 });
  const { isMinWidth } = useViewport();
  const L = useLocalizedStrings();
  const configuration = useContext(ConfigurationContext);
  const context = useContext(InterfaceContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const headerRef = useRef<HTMLDivElement>(null);

  const useCollapse = publicConfig.interface?.applicantFacingHeader?.hideOnScroll && !isMinWidth("md");

  const position = headerRef.current ? getComputedStyle(headerRef.current).position : null;
  useEffect(() => {
    if (useCollapse && isScrolling && headerRef.current && (position === "sticky" || position === "fixed")) {
      setCollapsed(scrollDirection === "down")
    }
  }, [useCollapse, position, headerRef.current, scrollDirection, isScrolling]);

  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.style.transform = `translateY(${(collapsed && useCollapse) ? -headerRef.current.offsetHeight : 0}px)`
    }
  }, [collapsed, useCollapse, headerRef.current]);

  const key = useLinkKey();

  // Prioritize the Distro program config, then the airtable config, otherwise use defaults
  const applicantFacingLogo = publicConfig.interface?.applicantFacingLogo?.url || configuration.applicant_facing_logo;
  const applicantFacingLogoWidth = publicConfig.interface?.applicantFacingLogo?.width || configuration.applicant_facing_logo_width || '150';
  const fewSteps = props.steps.length >= 9 ? (s: string) => '' : (s: string) => s;

  const programName = publicConfig.name || configuration.program_name || 'AidKit Program';

  const blockedBanner = props.blockedEditBanner
    ? typeof props.blockedEditBanner === 'object'
      ? props.blockedEditBanner[context.lang] || props.blockedEditBanner['en']
      : props.blockedEditBanner
    : undefined;
  const blockedBannerMarkdown = useModularMarkdown({
    content: blockedBanner || '',
    info: props.info || {},
  });
  const applicantBannerMarkdown = useMarkdown(context.banner?.[context.lang]);

  const isMobile = useIsMobile()

  const applicantPortal = props.applicantPortal;
  return (
    <>
      {!props.noNavBar &&
        <div ref={headerRef} className={"sticky top-0 touch-none md:relative md:touch-auto w-full z-50 transition duration-200 ease-in-out"}>
          <Disclosure>
            {(state: { open: boolean }) => (
              <>
                <header className="bg-white">
                  <div className="flex max-w-7xl mx-auto py-2 px-2 sm:px-6 lg:px-8 border-t-0 border-l-0 border-r-0 border-b border-solid border-gray-200 items-center justify-between gap-2 sm:gap-4">
                    {applicantPortal && isMobile && <SidebarNavTrigger className="!bg-gray-50 !text-gray-700 !font-normal" />}
                    <div className={"flex-shrink sm:flex-1 justify-self-start"}>
                      {props.branding?.logo && <img className="h-8 w-auto" src={props.branding.logo} alt={props.branding.name} />}
                      {!props.branding?.logo &&
                        <>{applicantFacingLogo
                          ?
                          <img
                            className="w-auto max-w-[150px] max-h-[125px]"
                            alt={programName}
                            src={applicantFacingLogo}
                            width={applicantFacingLogoWidth}
                          />
                          : (
                            <>
                              <div className="hidden sm:block">
                                <AidKitLogo width={100} height={40} />
                              </div>
                              <div className="block sm:hidden">
                                <MiniAidKitLogo width={40} height={40} />
                              </div>
                            </>
                          )}</>}
                    </div>
                    <div className={"hidden md:flex gap-2"}>
                      {props.sequential ?
                        <>
                          <div className="inline-block">
                            <div className={"center-hack mx-1 text-md sm:text-xs mt-1 text-gray-500 font-semibold tracking-wide uppercase "}>
                              {L.apply.step_of_steps.replace('$step', (props.steps.filter((s) => s.visible).indexOf(props.steps[props.currentStep]) + 1) + '').replace('$steps', (props.steps.filter((s) => s.visible).length) + '')}
                            </div>
                            <div className={"sm:block center-hack w-full text-sm " + fewSteps("md:text-xl") + " text-black font-medium"}>
                              {props.steps[props.currentStep]?.title}
                            </div>
                          </div>
                          <Disclosure.Button aria-expanded={state.open} aria-label="Application Step Menu" className={"bg-white h-10 flex-none rounded-md border-none " + fewSteps('hidden')}>
                            {state.open ?
                              <Icon name="ChevronUpIcon" role="decorative" /> :
                              <Icon name="ChevronDownIcon" role="decorative" />
                            }
                          </Disclosure.Button>
                        </> :
                        <>
                          <div className={"md:block center-hack " + fewSteps("center-hack-left") + " text-xl mt-2 ml-4 text-black font-medium "}>
                            {props.steps[props.currentStep]?.title}
                          </div>
                        </>
                      }
                    </div>
                    <div className="flex-shrink sm:flex-1 flex justify-content-end gap-1.5 sm:gap-2">
                      {key && <div className="hidden md:inline-block"><LogoutButton /></div>}
                      <LanguageDropdown {...props} />
                    </div>
                  </div>
                  <div className={"border-t-0 border-l-0 border-r-0 border-b border-solid border-gray-200 py-2 md:hidden"}>
                    {props.sequential ?
                      <div className="flex items-center justify-center gap-2 text-gray-500 font-semibold">
                        <div className={"mx-1 text-md mt-1 tracking-wide uppercase"}>
                          {L.apply.step_of_steps.replace('$step', (props.steps.filter((s) => s.visible).indexOf(props.steps[props.currentStep]) + 1) + '').replace('$steps', (props.steps.filter((s) => s.visible).length) + '')}
                        </div>
                        <Disclosure.Button aria-expanded={state.open} aria-label="Application Step Menu" className={"bg-white h-10 flex-none rounded-md border-none "}>
                          {state.open ?
                            <Icon name="ChevronUpIcon" role="decorative" /> :
                            <Icon name="ChevronDownIcon" role="decorative" />
                          }
                        </Disclosure.Button>
                      </div> :
                      <>
                        <div className={"md:block center-hack text-xl mt-2 ml-4 text-black font-medium " + fewSteps('md:hidden')}>
                          {props.steps[props.currentStep]?.title}
                        </div>
                      </>
                    }
                  </div>
                </header>
                <Disclosure.Panel className={"bg-white " + fewSteps('md:hidden')}>
                  {(cb: { close: () => void }) => (
                    <>{props.steps.filter((s) => s.visible).map((step, index) => {
                      if (!step.visitable) return null;
                      return <a
                        key={`step-${index}`}
                        href={window.location.pathname?.startsWith('/config') ? '' : '#'}
                        role="link"
                        aria-disabled={!step.visitable ? 'true' : undefined}
                        onClick={(e) => {
                          e.preventDefault();
                          if (step.visitable) { cb.close(); props.setCurrentStep(props.steps.indexOf(step)); }
                        }}
                        className={"flex-1 group py-2 flex flex-col mb-1 mt-1 border-b-0 border-solid " + (step.progressed ? "border-indigo-600" : "border-gray-100") + " border-t-0 " + (context.textAlign === 'right' ? "hover:border-r-indigo-800 border-r-4 border-l-0 pr-2" : "hover:border-l-indigo-800 border-l-4 border-r-0 pl-2")}
                      >
                        {props.sequential && <span className="text-xs text-indigo-600 font-semibold tracking-wide uppercase group-hover:text-indigo-800">
                          {L.apply.step_step.replace('$step', (index + 1) + '')}
                        </span>}
                        <span className={"text-black font-medium " + (props.sequential ? "text-sm" : "text-lg")}>{step.title}</span>
                      </a>;
                    })}
                    </>)}
                </Disclosure.Panel>
                <div className={"flex bg-white " + (state.open ? 'hidden ' + fewSteps('md:flex') : '')}>
                  {props.steps.filter((s) => s.visible).map((step, index) =>
                    <div
                      key={`div-step-${index}`}
                      className={"flex-1 flex group pl-2 " + fewSteps('md:py-2') + " flex flex-row ml-0.5 mr-0.5 border-b-0 border-r-0 border-solid " + ((props.sequential ? step.progressed : props.currentStep === index) ? "border-indigo-600" : "border-gray-100") + " hover:border-t-indigo-800 border-l-0 border-t-4"}
                    >
                      {props.sequential && <span className={"hidden " + fewSteps("md:flex") + " w-6 h-6 mt-1 mr-3 flex items-center justify-center rounded-full " +
                        (step.completed ? 'bg-indigo-600' : 'text-indigo-600 w-7 h-7 text-xs font-semibold border-solid border-2 border-indigo-600')}>
                        {step.completed === true ?
                          <CheckIcon className="w-4 h-4 text-white" aria-hidden="true" />
                          : (index + 1)}
                      </span>}
                      <a
                        href={window.location.pathname?.startsWith('/config') ? undefined : '#'}
                        onClick={(e) => {
                          e.preventDefault();
                          !props.blockedEditBanner && step.visitable && props.setCurrentStep(props.steps.indexOf(step));
                        }}
                        className={props.sequential ? "hidden " + fewSteps('md:block') : "m-auto hidden " + fewSteps('md:block')}>
                        {props.sequential && <span className={"hidden " + fewSteps('md:block') + " text-xs text-indigo-600 font-semibold tracking-wide uppercase group-hover:text-indigo-800"}>
                          {L.apply.step_step.replace('$step', (index + 1) + '')}
                        </span>}
                        <span className={"hidden " + fewSteps('md:block') + " text-black font-medium " + (props.sequential ? "text-sm" : "text-xl")}>{step.title}</span>
                      </a>
                    </div>
                  )}
                </div>
              </>)}
          </Disclosure>
        </div>}
      <div className={props.noNavBar ? 'pt-4' :
        ((context.banner?.[context.lang] || blockedBanner) ?
          "pt-8 md:pt-4" : "pt-8")}>
        {(context.banner?.[context.lang]) &&
          <div className={"rounded-md p-4 flex-1 flex flex-col " + context.banner?.['__style']}>
            {applicantBannerMarkdown}
          </div>}
        {blockedBanner &&
          <div className="flex rounded-md bg-yellow-50 border-2 border-yellow-300 p-2 justify-around">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-7 w-7 text-yellow-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <div className="text-sm text-yellow-700 -mb-4">
                  <div>{blockedBannerMarkdown}</div>
                </div>
              </div>
            </div>
          </div>}
        {props.steps[props.currentStep]?.children}
      </div>
    </>
  )
}

function LogoutButton(props: {}) {
  const cookieName = encodeURIComponent('key:' + window.location.pathname)
  const [cookies, ,] = useCookies(["auth_token", cookieName]);
  const publicConfig = useContext(PublicConfigurationContext);

  const L = useLocalizedStrings();

  return <button type="button"
    onClick={() => {
      if (cookies[cookieName]) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
      let redirect = publicConfig.application?.login?.logoutRedirectPath;
      if (redirect?.includes('http')) {
        window.location.href = redirect;
      } else if (redirect) {
        window.location.pathname = '/p/' + redirect;
      } else {
        window.location.href = 'https://aidkit.org';
      }
    }}
    className="rounded-md bg-indigo-50 px-2.5 py-1.5 my-1 h-8 border-0 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-100">
    {L.apply.close}
  </button>

}
